// Node clipboard for Ctrl+C / Ctrl+V copy-paste. Lives in localStorage so a
// copied node can be pasted into any workflow (other tabs included).
var { buildDuplicateNodeTitle, clonePlainData } = require('./nodeTypes');
var storageQuota = require('./storageQuota');

var STORAGE_KEY = 'alleflowNodeClipboard';

// Every node type is copyable — no exclusions.
function isCopyableNode(node) {
  return !!(node && node.id);
}

// The in-memory copy always works (paste in this tab, any workflow — the app
// is a SPA). localStorage/sessionStorage are best-effort extras for cross-tab
// paste; either can fail with QuotaExceeded when flow caches fill the origin.
var memoryClipboard = null;

// Copies the copyable nodes in the selection (plus the connections between
// them) to the clipboard. Returns how many nodes were copied.
function copySelection(nodes, connections, selectedIdMap) {
  var selected = (nodes || []).filter(function(node) {
    return node && selectedIdMap[node.id] && isCopyableNode(node);
  });
  if (!selected.length) {
    console.log('[copy-paste] Nothing to copy: canvas has ' + (nodes || []).length + ' node(s), none matched the selected ids', Object.keys(selectedIdMap || {}));
    return 0;
  }

  var idSet = {};
  selected.forEach(function(node) { idSet[node.id] = true; });
  var internalConnections = (connections || []).filter(function(conn) {
    return conn && idSet[conn.from] && idSet[conn.to];
  });

  var payload = {
    copiedAt: Date.now(),
    nodes: selected.map(function(node) {
      // Strip runtime output before cloning — it can be huge and is
      // meaningless in another workflow.
      var slim = Object.assign({}, node);
      delete slim.lastOutput;
      return clonePlainData(slim);
    }),
    connections: clonePlainData(internalConnections)
  };

  memoryClipboard = payload;
  var json = JSON.stringify(payload);
  // safeSetItem evicts disposable data and retries when the quota is full.
  if (!storageQuota.safeSetItem(STORAGE_KEY, json)) {
    console.log('[copy-paste] localStorage write failed — trying sessionStorage');
    try {
      window.sessionStorage.setItem(STORAGE_KEY, json);
    } catch (err2) {
      console.log('[copy-paste] sessionStorage write failed too (' + (err2 && err2.message) + ') — clipboard works in this tab only');
    }
  }
  return selected.length;
}

function parseClipboardJson(raw) {
  if (!raw) return null;
  try {
    var parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.nodes) && parsed.nodes.length ? parsed : null;
  } catch (err) {
    return null;
  }
}

// Newest copy wins across the three storages (memory / local / session).
function readClipboard() {
  var candidates = [memoryClipboard];
  try { candidates.push(parseClipboardJson(window.localStorage.getItem(STORAGE_KEY))); } catch (err) {}
  try { candidates.push(parseClipboardJson(window.sessionStorage.getItem(STORAGE_KEY))); } catch (err) {}
  var best = null;
  candidates.forEach(function(entry) {
    if (entry && (!best || (entry.copiedAt || 0) > (best.copiedAt || 0))) best = entry;
  });
  return best;
}

// Turns clipboard content into paste-ready nodes + connections: fresh ids,
// remapped connections, cleared duplicate-lock links, titles de-conflicted
// against the target workflow, and positions recentered on `center` while
// keeping the copied nodes' relative layout.
function buildPastePayload(clipboard, existingNodes, center) {
  var sourceNodes = clipboard.nodes || [];
  var seed = Date.now().toString(36);

  // Bounding-box center of the copied nodes, so the group lands on `center`.
  var minL = Infinity, minT = Infinity, maxL = -Infinity, maxT = -Infinity;
  sourceNodes.forEach(function(node) {
    var pos = node.position || { left: 0, top: 0 };
    if (pos.left < minL) minL = pos.left;
    if (pos.top < minT) minT = pos.top;
    if (pos.left > maxL) maxL = pos.left;
    if (pos.top > maxT) maxT = pos.top;
  });
  var offsetLeft = Math.round(center.left - (minL + maxL) / 2 - 110);
  var offsetTop = Math.round(center.top - (minT + maxT) / 2 - 24);

  var idMap = {};
  // Running pool so titles stay unique against the target workflow AND the
  // other nodes pasted in this batch.
  var titlePool = (existingNodes || []).slice();
  var takenTitles = {};
  titlePool.forEach(function(node) {
    takenTitles[String((node && node.title) || '').trim().toLowerCase()] = true;
  });

  var newNodes = sourceNodes.map(function(node, i) {
    var next = clonePlainData(node);
    idMap[node.id] = node.id + '-copy-' + seed + '-' + i;
    next.id = idMap[node.id];
    next.duplicateLocked = false;
    next.duplicateSourceNodeId = null;
    var pos = node.position || { left: 0, top: 0 };
    next.position = { left: pos.left + offsetLeft, top: pos.top + offsetTop };
    var title = String(node.title || '').trim();
    if (title && takenTitles[title.toLowerCase()]) {
      next.title = buildDuplicateNodeTitle(titlePool, title);
      next.label = next.title;
    }
    takenTitles[String(next.title || title).trim().toLowerCase()] = true;
    titlePool.push(next);
    return next;
  });

  var newConnections = (clipboard.connections || []).map(function(conn, i) {
    return Object.assign({}, clonePlainData(conn), {
      id: 'paste-conn-' + seed + '-' + i,
      from: idMap[conn.from],
      to: idMap[conn.to]
    });
  }).filter(function(conn) { return conn.from && conn.to; });

  return { nodes: newNodes, connections: newConnections };
}

module.exports = {
  copySelection: copySelection,
  readClipboard: readClipboard,
  buildPastePayload: buildPastePayload
};
